import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import NoServiceAccounts from 'src/assets/no-service-accounts.svg';
import Avatar from 'src/components/ui/Avatar/Avatar';
import Button from 'src/components/ui/Button/Button';
import EmptyState from 'src/components/ui/EmptyState/EmptyState';
import PageHeader from 'src/components/ui/PageHeader/PageHeader';
import Switch from 'src/components/ui/Switch/Switch';
import Table from 'src/components/ui/Table/Table';
import {
  ServiceAccountTableQueryParameters,
  TableHeader,
} from 'src/components/ui/Table/Table.interface';
import TablePagination from 'src/components/ui/Table/TablePagination/TablePagination';
import { paginationDefaultState } from 'src/constants/paginationConstants';
import { routes } from 'src/routes';
import {
  changeServiceAccountStatus,
  getServiceAccountList,
  setServiceAccountList,
} from 'src/store/actions/serviceAccountActions';
import {
  ServiceAccount,
  ServiceAccountAccess,
} from 'src/store/models/ServiceAccount';
import { useLoadingSelector } from 'src/utils/useLoadingSelector';
import { useQueryParams } from 'src/utils/useQueryParams';

import classes from './ServiceAccounts.module.scss';

const ServiceAccounts: FC = () => {
  const location = useLocation();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const serviceAccountList = useAppSelector(
    (state) => state.serviceAccount.serviceAccountList
  );
  const loading = useLoadingSelector({
    actionType: getServiceAccountList.toString(),
  });

  const headers: TableHeader<ServiceAccount>[] = [
    {
      accessor: 'title',
      label: t('common:name'),
      sortable: true,
      component: (account) => (
        <Avatar
          title={account?.title}
          image={account?.image}
          imageSize={32}
          className={classes.Avatar}
        />
      ),
    },
    {
      accessor: 'company',
      label: t('common:company'),
      sortable: true,
      sortKey: 'company.title',
      component: (account) => account?.company?.title,
    },
    {
      accessor: 'eMail',
      label: t('common:email'),
      sortable: true,
    },
    {
      accessor: 'phone',
      label: t('common:phone'),
      sortable: true,
    },
    {
      accessor: 'restricted',
      label: t('common:access'),
      sortable: true,
      component: (account) =>
        t(
          `common:${
            account?.restricted
              ? ServiceAccountAccess.LIMITED
              : ServiceAccountAccess.FULL
          }`
        ),
    },
    {
      accessor: 'enabled',
      label: t('common:active'),
      sortable: true,
      component: (account) => (
        <Switch
          checked={account?.enabled}
          handleChange={(_, checked) =>
            dispatch(
              changeServiceAccountStatus({ service: account, enabled: checked })
            )
          }
          loading={{
            actionType: changeServiceAccountStatus.toString(),
            id: account?.id,
          }}
        />
      ),
    },
  ];

  const { urlParams } = useQueryParams<ServiceAccountTableQueryParameters>(
    location.search,
    {
      ...paginationDefaultState,
    }
  );

  useEffect(() => {
    dispatch(setServiceAccountList({ count: 0, data: [], ctx: null }));
    return () => {
      dispatch(setServiceAccountList({ count: 0, data: [], ctx: null }));
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    dispatch(getServiceAccountList({ params: urlParams }));
    // eslint-disable-next-line
  }, [location]);

  return (
    <div className={classes.Container}>
      <PageHeader
        title={t('common:service')}
        endNode={
          <Button
            onClick={() => navigate(routes.NEW_SERVICE_ACCOUNT)}
            endIcon={<FontAwesomeIcon icon={faPlus} />}
            className={classes.HeaderButton}
          >
            {t('common:create_new')}
          </Button>
        }
      />
      <Table
        headers={headers}
        data={serviceAccountList?.data}
        search
        loading={loading}
        className={classNames(classes.Table, { [classes.Loading]: loading })}
        classes={{ header: classes.TableHeader }}
        onRowClick={(row) =>
          navigate(`${routes.EDIT_SERVICE_ACCOUNT}/${row?.id}`)
        }
        emptyTableComponent={
          <EmptyState
            title={t('service_account:no_accounts')}
            subtitle={t('service_account:no_accounts_details')}
            icon={NoServiceAccounts}
          />
        }
        searchPlaceholder={t('common:search_field')}
      />
      <TablePagination
        className={classes.Pagination}
        urlParams={urlParams}
        total={serviceAccountList?.count}
      />
    </div>
  );
};

export default ServiceAccounts;
